import { Injectable, Signal, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
	selectFeatureFlags,
	selectFeatureFlag,
	selectFeatureFlagsLoaded,
} from "../store/feature-flags.selectors";
import { FeatureFlag, FeatureFlags } from "../models/feature-flags.model";
import { LoadFeatureFlagsAction, SetFeatureFlagsAction } from "../store/feature-flags.actions";
import { HttpClient } from "@angular/common/http";

/**
 * Service for accessing feature flags in components and services
 * Provides convenient methods for checking feature flags
 */
@Injectable({
	providedIn: "root",
})
export class FeatureFlagsService {
	private store = inject(Store);
	private http = inject(HttpClient);

	/**
	 * Get all feature flags as an observable
	 */
	getFeatureFlags$(): Observable<FeatureFlags> {
		return this.store.select(selectFeatureFlags);
	}

	/**
	 * Check if a specific feature flag is enabled
	 * @param flagName Feature flag to check
	 * @returns Observable<boolean> indicating if the flag is enabled
	 */
	isFeatureEnabled$(flagName: FeatureFlag): Observable<boolean> {
		return this.store.select(selectFeatureFlag(flagName)).pipe(map((enabled) => enabled === true));
	}
	/**
	 * Check if a specific feature flag is enabled
	 * @param flagName Feature flag to check
	 * @returns Signal<boolean> indicating if the flag is enabled
	 */
	getFeatureFlagSignal(flagName: FeatureFlag): Signal<boolean> {
		return this.store.selectSignal(selectFeatureFlag(flagName));
	}

	/**
	 * Synchronously check if a feature flag is enabled
	 * @param flagName Feature flag to check
	 * @returns boolean indicating if the flag is enabled (defaults to false)
	 */
	isFeatureEnabled(flagName: FeatureFlag): boolean {
		const flags = this.store.selectSignal(selectFeatureFlags)();
		return flags[flagName] === true;
	}

	/**
	 * Check if feature flags have been loaded
	 */
	isLoaded$(): Observable<boolean> {
		return this.store.select(selectFeatureFlagsLoaded);
	}

	/**
	 * Load feature flags from remote source (for future use)
	 */
	loadFeatureFlags(): void {
		this.store.dispatch(new LoadFeatureFlagsAction());
	}

	/**
	 * Set feature flags directly (useful for testing or manual override)
	 * @param flags Feature flags to set
	 */
	setFeatureFlags(flags: FeatureFlags): void {
		this.store.dispatch(new SetFeatureFlagsAction(flags));
	}

	/**
	 * Fetch feature flags from remote source
	 */
	fetchFeatureFlags(): Observable<FeatureFlags> {
		return this.http.get<FeatureFlags>("/api/v1/feature-flags");
	}
}
